import type { PlayerSettings } from "./types";

// Rendered inside a shadow root, so landing-page CSS can't break the player.
export function playerCss(s: PlayerSettings): string {
  const [w, h] = s.aspectRatio.split(":").map(Number);
  const ratio = w > 0 && h > 0 ? (h / w) * 100 : 56.25;
  const b = s.playButton;
  const u = s.unmute;
  const btnRadius = b.shape === "circle" ? "50%" : b.shape === "rounded" ? "22%" : "4px";
  return `
:host{display:block;position:relative;width:100%}
*{box-sizing:border-box}
.box{position:relative;width:100%;padding-top:${ratio}%;background:#000;border-radius:${s.radius}px;overflow:hidden;font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;-webkit-tap-highlight-color:transparent;user-select:none}
.box:fullscreen{padding-top:0;border-radius:0}
video{position:absolute;inset:0;width:100%;height:100%;object-fit:contain;background:#000}
.poster{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;pointer-events:none}
.layer{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;cursor:pointer;z-index:3}
.hidden{display:none!important}
.play{width:${b.size}px;height:${b.size}px;border-radius:${btnRadius};background:${b.color};display:flex;align-items:center;justify-content:center;box-shadow:0 8px 30px rgba(0,0,0,.35);transition:transform .15s}
.play:hover{transform:scale(1.06)}
.play svg{width:42%;height:42%;margin-left:8%;fill:${b.iconColor}}
.pulse{animation:vslring 1.6s ease-out infinite}
@keyframes vslring{0%{box-shadow:0 0 0 0 color-mix(in srgb,${b.color} 70%,transparent)}75%,100%{box-shadow:0 0 0 ${Math.round(b.size / 3)}px color-mix(in srgb,${b.color} 0%,transparent)}}
.beat{animation:vslbeat 1.4s ease-in-out infinite}
@keyframes vslbeat{0%,100%{transform:scale(1)}50%{transform:scale(1.06)}}
@media (prefers-reduced-motion:reduce){.pulse,.beat{animation:none}}
.ptext{color:${b.textColor};font-size:${b.textSize}px;font-weight:700;text-shadow:0 2px 8px rgba(0,0,0,.6);text-align:center;padding:0 16px}
.unmute{background:${u.bg};color:${u.textColor};border:1px solid rgba(255,255,255,.85);border-radius:6px;padding:clamp(12px,3vw,22px) clamp(16px,4vw,34px);width:min(86%,440px);display:flex;flex-direction:column;align-items:center;gap:clamp(6px,1.6vw,12px);text-align:center;box-shadow:0 10px 30px rgba(0,0,0,.35);font-weight:700;line-height:1.2}
.unmute b{display:block;font-size:clamp(16px,4.2vw,26px)}
.unmute span{display:block;font-size:clamp(15px,3.8vw,24px)}
.unmute svg{width:clamp(64px,17vw,120px);height:auto;overflow:visible}
.unmute .spk{fill:${u.textColor}}
.unmute .wv,.unmute .x{fill:none;stroke:${u.textColor};stroke-linecap:round}
.unmute .wv{stroke-width:2.6;animation:vslwave 1.5s ease-in-out infinite}
.unmute .w2{animation-delay:.2s}
.unmute .w3{animation-delay:.4s}
.unmute .x{stroke-width:3.2}
@keyframes vslwave{0%,100%{opacity:.25}50%{opacity:1}}
@media (prefers-reduced-motion:reduce){.unmute .wv{animation:none}}
.resume{background:rgba(0,0,0,.72);cursor:default}
.resume b{color:#fff;font-size:clamp(15px,3.6vw,20px);text-align:center;padding:0 16px}
.rbtn{border:0;border-radius:10px;padding:12px 20px;font-size:15px;font-weight:700;cursor:pointer;min-width:190px;display:flex;align-items:center;justify-content:center;gap:8px}
.rbtn.main{background:${s.color};color:#fff}
.rbtn.alt{background:#fff;color:#111}
.rbtn svg{width:16px;height:16px;fill:currentColor}
.bar{position:absolute;left:0;right:0;bottom:0;height:${s.progressHeight}px;background:rgba(255,255,255,.25);z-index:4}
.bar.seek{cursor:pointer;height:${Math.max(s.progressHeight, 10)}px}
.fill{height:100%;width:0;background:${s.color};transition:width .25s linear}
.ctrls{position:absolute;right:8px;bottom:${s.progressHeight + 8}px;display:flex;gap:6px;z-index:4;align-items:center}
.ctrls button{background:rgba(0,0,0,.55);border:0;border-radius:8px;width:36px;height:36px;display:flex;align-items:center;justify-content:center;cursor:pointer;padding:0}
.ctrls svg{width:20px;height:20px;fill:#fff}
.time{color:#fff;font-size:12px;background:rgba(0,0,0,.55);border-radius:8px;padding:4px 8px}
.spin{position:absolute;left:50%;top:50%;width:44px;height:44px;margin:-22px 0 0 -22px;border:4px solid rgba(255,255,255,.25);border-top-color:#fff;border-radius:50%;animation:vslspin .8s linear infinite;z-index:2}
@keyframes vslspin{to{transform:rotate(360deg)}}
`;
}

export const ICONS = {
  play: '<svg viewBox="0 0 24 24"><path d="M6 4.5v15a1 1 0 0 0 1.5.86l12.5-7.5a1 1 0 0 0 0-1.72L7.5 3.64A1 1 0 0 0 6 4.5z"/></svg>',
  muted:
    '<svg viewBox="0 0 24 24"><path d="M3 9v6h4l5 5V4L7 9H3zm13.59 3L19 9.59 17.59 8.17 15.17 10.6 12.76 8.17 11.34 9.59 13.76 12l-2.42 2.41 1.42 1.42 2.41-2.42 2.42 2.42L19 14.41 16.59 12z"/></svg>',
  sound:
    '<svg viewBox="0 0 24 24"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0 0 14 7.97v8.05A4.5 4.5 0 0 0 16.5 12zM14 3.23v2.06a7 7 0 0 1 0 13.42v2.06a9 9 0 0 0 0-17.54z"/></svg>',
  // speaker + three animated waves + slash (the "sound is off" card)
  unmute:
    '<svg viewBox="0 0 64 44"><path class="spk" d="M4 15h9l12-10v34L13 29H4z"/><path class="wv w1" d="M31 16a8 8 0 0 1 0 12"/><path class="wv w2" d="M37 11a15 15 0 0 1 0 22"/><path class="wv w3" d="M43 6a22 22 0 0 1 0 32"/><path class="x" d="M8 4l40 36"/></svg>',
  pause: '<svg viewBox="0 0 24 24"><path d="M6 5h4v14H6zm8 0h4v14h-4z"/></svg>',
  fs: '<svg viewBox="0 0 24 24"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/></svg>',
  restart:
    '<svg viewBox="0 0 24 24"><path d="M12 5V1L7 6l5 5V7a6 6 0 1 1-6 6H4a8 8 0 1 0 8-8z"/></svg>',
};
